import React, { useMemo, useState } from "react";
import { useQuote } from "../context/QuoteContext";
import api from "../api";
import { buildWhatsAppMessage, formatEur, copyText, waMarkupToHtml } from "../utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Copy, Check, Cloud, CloudOff, Loader2, User, Save, CopyPlus, Sparkles, RotateCcw, SlidersHorizontal, Pencil, Eye, Scissors } from "lucide-react";
import { toast } from "sonner";

// Render WhatsApp markdown: *bold* and _italic_ (non-nested)
const renderFormatted = (line, i) => {
  const nodes = [];
  let buf = "";
  let bold = false, italic = false, key = 0;
  const flush = () => {
    if (!buf) return;
    let node;
    if (bold && italic) node = <strong key={key}><em>{buf}</em></strong>;
    else if (bold) node = <strong key={key}>{buf}</strong>;
    else if (italic) node = <em key={key} className="text-[#54656f]">{buf}</em>;
    else node = <span key={key}>{buf}</span>;
    nodes.push(node); key += 1; buf = "";
  };
  for (const ch of line) {
    if (ch === "*") { flush(); bold = !bold; }
    else if (ch === "_") { flush(); italic = !italic; }
    else buf += ch;
  }
  flush();
  return <div key={i} className="min-h-[6px]">{nodes}</div>;
};

export default function WhatsAppPreview() {
  const {
    mode, items, areaData, monthlyTotal, ritiro, ritiroTotal, discounts,
    clienteNome, setClienteNome, saveStatus, currentQuoteId,
    savedQuoteNum, saveNow, saveAsNew, imballaggioTotal, smontaggioTotal, settings,
    polishedMessage, setPolishedMessage, abitiBox, setAbitiBox, freeMaterials, setFreeMaterials, catalog,
    adaptedWhy, setAdaptedWhy, inputText, copiedFrom, duplicateCurrent, chatMessage,
  } = useQuote();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [msgPreview, setMsgPreview] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitParts, setSplitParts] = useState([]);
  const [splitting, setSplitting] = useState(false);
  const [copiedPart, setCopiedPart] = useState(-1);
  const [polishInput, setPolishInput] = useState("");
  const [polishing, setPolishing] = useState(false);
  const [genTextAi, setGenTextAi] = useState(false);
  const [adaptingWhy, setAdaptingWhy] = useState(false);
  const isTextMode = mode === "text" || mode === "text_done";
  const textAiEnabled = settings?.textAiEnabled === true || settings?.textAiEnabled === "true";

  const abitiPrice = useMemo(() => {
    if (settings?.msgAbitiPrice != null) return Number(settings.msgAbitiPrice);
    const c = (catalog || []).find((x) => /porta abiti/i.test(x.label || ""));
    return c ? c.price_eur : 18;
  }, [settings, catalog]);

  const message = useMemo(
    () => {
      if (mode === "text_simple") {
        // Once the deep analysis has populated fields, build the message from them
        // (lists all items, including a "Deposito per volume" line for the area part).
        if (items.length > 0) {
          return buildWhatsAppMessage({ mode: "objects", items, areaData, monthlyTotal, ritiro, ritiroTotal, allDiscounts: discounts, durationDiscounts: settings?.durationDiscounts, smontaggio: ritiro.smontaggio, smontaggioTotal, imballaggio: ritiro.imballaggio, imballaggioTotal, texts: settings, abitiBox, abitiPrice, freeMaterials, adaptedWhy: "" });
        }
        return chatMessage || "";
      }
      return buildWhatsAppMessage({ mode, items, areaData, monthlyTotal, ritiro, ritiroTotal, allDiscounts: discounts, durationDiscounts: settings?.durationDiscounts, smontaggio: ritiro.smontaggio, smontaggioTotal, imballaggio: ritiro.imballaggio, imballaggioTotal, texts: settings, abitiBox, abitiPrice, freeMaterials, adaptedWhy: isTextMode ? adaptedWhy : "" });
    },
    [mode, chatMessage, items, areaData, monthlyTotal, ritiro, ritiroTotal, discounts, imballaggioTotal, smontaggioTotal, settings, abitiBox, abitiPrice, freeMaterials, adaptedWhy, isTextMode]
  );

  const displayMessage = (polishedMessage && polishedMessage.trim()) ? polishedMessage : message;

  const copy = async () => {
    const ok = await copyText(displayMessage, waMarkupToHtml(displayMessage));
    if (ok) {
      setCopied(true);
      toast.success("Messaggio copiato con formattazione!");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Impossibile copiare");
    }
  };

  const doSplit = async () => {
    setSplitting(true);
    try {
      const { data } = await api.post("/ai/split-message", { message: displayMessage });
      const parts = (data.parts || []).filter((p) => (p.text || "").trim());
      if (parts.length) {
        setSplitParts(parts);
        setSplitOpen(true);
        toast.success(`Messaggio diviso in ${parts.length} parti`);
      } else {
        toast.error("Impossibile dividere il messaggio");
      }
    } catch {
      toast.error("Impossibile dividere il messaggio");
    } finally {
      setSplitting(false);
    }
  };

  const copyPart = async (idx, text) => {
    const ok = await copyText(text, waMarkupToHtml(text));
    if (ok) {
      setCopiedPart(idx);
      toast.success(`Parte ${idx + 1} copiata`);
      setTimeout(() => setCopiedPart(-1), 2000);
    } else {
      toast.error("Impossibile copiare");
    }
  };

  const polish = async () => {
    setPolishing(true);
    try {
      const { data } = await api.post("/ai/polish-message", { message: displayMessage, instruction: polishInput, prompt: settings?.msgPolishPrompt || "" });
      if (data.polished) {
        setPolishedMessage(data.polished);
        toast.success("Messaggio ottimizzato con l'AI");
      }
    } catch {
      toast.error("Impossibile ottimizzare il messaggio");
    } finally {
      setPolishing(false);
    }
  };

  const revertPolish = () => {
    setPolishedMessage("");
    setPolishInput("");
    toast.success("Ripristinato il messaggio originale");
  };

  const updateFromData = () => {
    setPolishedMessage("");
    toast.success("Messaggio aggiornato con gli ultimi dati");
  };
  const messageStale = mode !== "text_simple" && !!(polishedMessage && polishedMessage.trim()) && displayMessage !== message;

  const generateTextAi = async () => {
    setGenTextAi(true);
    try {
      const { data } = await api.post("/ai/text-message", { message: displayMessage, instruction: polishInput });
      if (data.message) {
        setPolishedMessage(data.message);
        toast.success("Messaggio generato con l'AI dalle istruzioni");
      }
    } catch {
      toast.error("Impossibile generare il messaggio");
    } finally {
      setGenTextAi(false);
    }
  };

  const adaptWhy = async () => {
    setAdaptingWhy(true);
    try {
      const { data } = await api.post("/ai/adapt-why", { clientRequest: inputText || "", whyText: settings?.msgWhy || "" });
      if (data.adaptedWhy) {
        setAdaptedWhy(data.adaptedWhy);
        toast.success("Vantaggi adattati alla richiesta del cliente");
      }
    } catch {
      toast.error("Impossibile adattare i vantaggi");
    } finally {
      setAdaptingWhy(false);
    }
  };

  const revertWhy = () => {
    setAdaptedWhy("");
    toast.success("Ripristinati i vantaggi predefiniti");
  };

  const doSave = async () => {
    setBusy(true);
    const ok = await saveNow();
    setBusy(false);
    if (ok) toast.success(savedQuoteNum ? `Stima #${savedQuoteNum} salvata` : "Stima salvata");
    else toast.error("Errore durante il salvataggio");
  };
  const doSaveAsNew = async () => {
    setBusy(true);
    const ok = await saveAsNew();
    setBusy(false);
    if (ok) toast.success("Salvata come nuova stima");
    else toast.error("Errore durante il salvataggio");
  };

  const statusMap = {
    idle: { icon: <Cloud className="h-3.5 w-3.5" />, text: "Auto-salvataggio attivo", cls: "text-slate-400" },
    saving: { icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />, text: "Salvataggio...", cls: "text-slate-500" },
    saved: { icon: <Cloud className="h-3.5 w-3.5" />, text: "Preventivo salvato", cls: "text-green-600" },
    error: { icon: <CloudOff className="h-3.5 w-3.5" />, text: "Errore salvataggio", cls: "text-red-500" },
  };
  const st = statusMap[saveStatus] || statusMap.idle;
  const nowTime = new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const contactName = (clienteNome && clienteNome.trim()) ? clienteNome : "+39 346 032 4487";

  return (
    <div className="lg:sticky lg:top-24 w-full lg:w-[340px] shrink-0" data-testid="whatsapp-preview">
      <div className="rounded-lg overflow-hidden border border-slate-200 shadow-sm bg-white">
        {/* WhatsApp light contact header */}
        <div className="flex items-center gap-3 px-3 py-2.5 bg-white border-b border-slate-100">
          <div className="h-10 w-10 rounded-full bg-[#dfd3f3] flex items-center justify-center shrink-0">
            <User className="h-5 w-5 text-[#8b7ec8]" fill="#8b7ec8" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold text-[#111b21] leading-tight truncate">{contactName}</div>
            <div className="text-xs text-[#667781] leading-tight truncate">ultimo accesso oggi alle {nowTime}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={copy} data-testid="whatsapp-copy-button"
            className="h-8 text-[#1a5f5a] hover:bg-[#1a5f5a]/10 hover:text-[#134744] gap-1.5 shrink-0">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copiato" : "Copia"}
          </Button>
        </div>

        <div className="wa-chat-bg p-3 min-h-[260px] max-h-[520px] overflow-y-auto flex flex-col">
          <div className="wa-bubble ml-auto max-w-[85%] px-2.5 py-1.5 mt-1 mr-2 text-[13.5px] leading-snug"
            data-testid="whatsapp-message">
            {displayMessage.split("\n").map(renderFormatted)}
            <div className="text-[10px] text-[#667781] text-right mt-1 flex items-center justify-end gap-0.5">
              {nowTime}
              <svg width="15" height="11" viewBox="0 0 16 11" className="inline-block" fill="none">
                <path d="M11.07.653a.5.5 0 0 0-.706.03L5.1 6.38 3.4 4.6a.5.5 0 1 0-.72.694l2.06 2.14a.5.5 0 0 0 .72 0l5.64-6.06a.5.5 0 0 0-.03-.72z" fill="#53bdeb"/>
                <path d="M15.07.653a.5.5 0 0 0-.706.03L9.1 6.38l-.62-.65-.72.694 1 1.04a.5.5 0 0 0 .72 0l5.64-6.06a.5.5 0 0 0-.03-.72z" fill="#53bdeb"/>
              </svg>
            </div>
          </div>
        </div>

        {messageStale && (
          <div className="px-3 py-2 bg-amber-50 border-t border-amber-100 flex items-center justify-between gap-2" data-testid="message-stale-banner">
            <span className="text-[11px] text-amber-700 leading-tight">Il messaggio non riflette le ultime modifiche ai dati.</span>
            <Button size="sm" variant="ghost" onClick={updateFromData} data-testid="update-message-from-data"
              className="h-7 shrink-0 text-amber-700 hover:bg-amber-100 hover:text-amber-800 gap-1.5">
              <RotateCcw className="h-3.5 w-3.5" /> Aggiorna dai dati
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#134744] bg-[#1a5f5a]/10 px-2.5 py-1 rounded-full" data-testid="stima-id">
              {savedQuoteNum ? `Stima #${savedQuoteNum}` : "Nuova stima"}
            </span>
            {copiedFrom ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-100 px-2 py-1 rounded-full" data-testid="copied-from-badge">
                <CopyPlus className="h-3 w-3" /> Copia di #{copiedFrom}
              </span>
            ) : null}
          </div>
          <div className={`flex items-center gap-1.5 text-xs ${st.cls}`} data-testid="save-status">
            {st.icon}
            <span>{saveStatus === "saved" && savedQuoteNum ? `Stima #${savedQuoteNum} salvata` : st.text}</span>
          </div>
        </div>

        <Input value={clienteNome} onChange={(e) => setClienteNome(e.target.value)}
          placeholder="Nome cliente (opzionale)" data-testid="cliente-nome-input" />

        <div className="flex gap-2">
          <Button onClick={doSave} disabled={busy || mode === null} data-testid="save-button"
            className="flex-1 bg-[#1a5f5a] hover:bg-[#134744] text-white gap-1.5 h-9">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
          </Button>
          <Button onClick={doSaveAsNew} disabled={busy || mode === null} variant="outline" data-testid="save-as-new-button"
            className="flex-1 gap-1.5 h-9">
            <CopyPlus className="h-4 w-4" /> Salva come nuovo
          </Button>
        </div>

        {currentQuoteId ? (
          <Button onClick={duplicateCurrent} variant="outline" data-testid="duplicate-current-button"
            className="w-full gap-1.5 h-9 border-amber-300 text-amber-700 hover:bg-amber-50">
            <Copy className="h-4 w-4" /> Duplica con soglie attuali
          </Button>
        ) : null}

        <Button onClick={() => setAdjustOpen(true)} variant="outline" data-testid="adjust-message-button"
          className="w-full gap-1.5 h-9 border-[#1a5f5a]/40 text-[#1a5f5a] hover:bg-[#1a5f5a]/5">
          <SlidersHorizontal className="h-4 w-4" /> Regola messaggio
        </Button>

        <Button onClick={doSplit} disabled={splitting || mode === null} variant="outline" data-testid="split-message-button"
          className="w-full gap-1.5 h-9 border-[#1a5f5a]/40 text-[#1a5f5a] hover:bg-[#1a5f5a]/5">
          {splitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />} Dividi messaggio
        </Button>

        <Dialog open={splitOpen} onOpenChange={setSplitOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="split-message-dialog">
            <DialogHeader>
              <DialogTitle>Messaggio diviso in {splitParts.length} parti</DialogTitle>
            </DialogHeader>
            <p className="text-[11px] text-slate-400 -mt-2">Copia e invia ogni parte separatamente su WhatsApp.</p>
            <div className="space-y-3">
              {splitParts.map((p, idx) => (
                <div key={idx} className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid={`split-part-${idx}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-[#134744] truncate">Parte {idx + 1}{p.title ? ` · ${p.title}` : ""}</span>
                    <Button size="sm" variant="ghost" onClick={() => copyPart(idx, p.text)} data-testid={`split-copy-${idx}`}
                      className="h-7 shrink-0 text-[#1a5f5a] hover:bg-[#1a5f5a]/10 hover:text-[#134744] gap-1.5">
                      {copiedPart === idx ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copiedPart === idx ? "Copiato" : "Copia"}
                    </Button>
                  </div>
                  <div className="wa-chat-bg rounded-md p-2.5">
                    <div className="wa-bubble max-w-full px-2.5 py-1.5 text-[13.5px] leading-snug">
                      {p.text.split("\n").map(renderFormatted)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="adjust-message-dialog">
            <DialogHeader>
              <DialogTitle>Regola messaggio</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
              {/* editable message — right column */}
              <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2 order-1 md:order-2" data-testid="editable-message-panel">
                <div className="flex items-center justify-between gap-2">
                  <div className="inline-flex items-center rounded-md bg-slate-100 p-0.5" data-testid="msg-mode-toggle">
                    <button onClick={() => setMsgPreview(false)} data-testid="msg-mode-edit"
                      className={`px-2.5 py-1 text-[11px] font-medium rounded flex items-center gap-1 transition-colors ${!msgPreview ? "bg-white text-[#134744] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                      <Pencil className="h-3 w-3" /> Modifica
                    </button>
                    <button onClick={() => setMsgPreview(true)} data-testid="msg-mode-preview"
                      className={`px-2.5 py-1 text-[11px] font-medium rounded flex items-center gap-1 transition-colors ${msgPreview ? "bg-white text-[#134744] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                      <Eye className="h-3 w-3" /> Anteprima
                    </button>
                  </div>
                  {polishedMessage && polishedMessage.trim() && (
                    <button onClick={revertPolish} className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="editable-message-revert">
                      <RotateCcw className="h-3 w-3" /> Ripristina originale
                    </button>
                  )}
                </div>
                {msgPreview ? (
                  <div className="wa-chat-bg rounded-md p-3 min-h-[300px] max-h-[420px] overflow-y-auto" data-testid="msg-preview-render">
                    <div className="wa-bubble max-w-full px-2.5 py-1.5 text-[13.5px] leading-snug">
                      {displayMessage.split("\n").map(renderFormatted)}
                    </div>
                  </div>
                ) : (
                  <Textarea value={displayMessage} onChange={(e) => setPolishedMessage(e.target.value)}
                    rows={18} className="text-sm resize-none font-mono" data-testid="adjust-message-textarea" />
                )}
                <p className="text-[11px] text-slate-400">Usa *grassetto* e _corsivo_ come su WhatsApp.</p>
              </div>

              {/* options — left column */}
              <div className="space-y-3 order-2 md:order-1">
              <Button onClick={doSplit} disabled={splitting || mode === null} variant="outline" data-testid="split-message-button-dialog"
                className="w-full gap-1.5 h-9 border-[#1a5f5a]/40 text-[#1a5f5a] hover:bg-[#1a5f5a]/5">
                {splitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />} Dividi in più messaggi
              </Button>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="abiti-toggle-row">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-700">👗 Sezione scatola porta abiti</div>
                  <div className="text-[11px] text-slate-400">Attivata automaticamente se la richiesta cita capi/vestiti</div>
                </div>
                <Switch checked={abitiBox} onCheckedChange={setAbitiBox} data-testid="abiti-toggle" />
              </div>

              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="free-materials-toggle-row">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-700">🎁 Sezione materiali d'imballaggio gratuiti</div>
                  <div className="text-[11px] text-slate-400">Includi il blocco sui materiali d'imballaggio gratuiti nel messaggio</div>
                </div>
                <Switch checked={freeMaterials} onCheckedChange={setFreeMaterials} data-testid="free-materials-toggle" />
              </div>

              {isTextMode && settings?.msgWhy && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="adapt-why-panel">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#134744] flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5" /> Adatta i vantaggi alla richiesta
                    </span>
                    {adaptedWhy && adaptedWhy.trim() && (
                      <button onClick={revertWhy} className="text-[11px] text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="adapt-why-revert-button">
                        <RotateCcw className="h-3 w-3" /> Ripristina predefiniti
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400">
                    L'AI riscrive la sezione "Perché scegliere StorMi" in base al testo della richiesta del cliente.
                  </p>
                  <Button onClick={adaptWhy} disabled={adaptingWhy} data-testid="adapt-why-button"
                    className="w-full bg-[#1a5f5a] hover:bg-[#134744] text-white gap-1.5 h-9">
                    {adaptingWhy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {adaptedWhy && adaptedWhy.trim() ? "Rigenera vantaggi" : "Adatta vantaggi"}
                  </Button>
                </div>
              )}

              {textAiEnabled && (
                <div className="rounded-lg border border-[#1a5f5a]/40 bg-[#1a5f5a]/5 p-3 space-y-2" data-testid="text-ai-panel">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#134744] flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5" /> Genera messaggio con AI (istruzioni)
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Genera il messaggio WhatsApp seguendo le istruzioni configurate in Impostazioni, invece del template fisso. Puoi aggiungere indicazioni nel campo qui sotto.
                  </p>
                  <Button onClick={generateTextAi} disabled={genTextAi || mode === null} data-testid="text-ai-button"
                    className="w-full bg-[#1a5f5a] hover:bg-[#134744] text-white gap-1.5 h-9">
                    {genTextAi ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {polishedMessage && polishedMessage.trim() ? "Rigenera messaggio" : "Genera messaggio"}
                  </Button>
                </div>
              )}

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="polish-panel">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#134744] flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> Ottimizza con l'AI
                  </span>
                </div>
                <Textarea value={polishInput} onChange={(e) => setPolishInput(e.target.value)}
                  rows={2} placeholder="Istruzioni opzionali (es. tono più caloroso, enfatizza la sicurezza)…"
                  className="text-sm resize-none bg-white" data-testid="polish-input" />
                <Button onClick={polish} disabled={polishing || mode === null} data-testid="polish-button"
                  className="w-full bg-[#1a5f5a] hover:bg-[#134744] text-white gap-1.5 h-9">
                  {polishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {polishedMessage && polishedMessage.trim() ? "Rigenera con l'AI" : "Ottimizza con l'AI"}
                </Button>
              </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
